import { CornerDownLeft } from 'lucide-react';
import { cn } from '@scio3/ui';

interface EnterHintProps {
  className?: string;
}

/**
 * The ⏎ key chip that appears at the right edge of the ACTIVE palette row.
 *
 * Every row in the command palette does something different when opened (a
 * screen deep-links into Screens, a nav target jumps to a settings page), so
 * the palette needs to say that Enter is what commits the highlighted row —
 * otherwise arrow-navigating just moves a highlight with no stated payoff.
 *
 * Visibility is driven by cmdk, not by React state: cmdk stamps
 * `data-selected="true"` on the active `CommandItem`, so the chip only needs
 * the parent to carry `group` and it reveals itself. That keeps arrow-key
 * navigation entirely inside cmdk (no re-render per keypress) and means the
 * chip also shows on hover, since cmdk treats hover as selection.
 *
 * It toggles `invisible` → `visible`, NOT `hidden` → `flex`: the chip is
 * always in the layout. Revealing it via `display` re-flows the row on every
 * hover, which reads as the whole list jiggling as the cursor moves down it.
 * The rows also carry `min-h-9` so the chip can never be what sets their
 * height.
 */
export function EnterHint({ className }: EnterHintProps) {
  return (
    <span
      aria-hidden
      data-testid="palette-enter-hint"
      className={cn(
        // `bg-card`, not `bg-muted`: this chip is ONLY ever visible on the
        // selected row, and that row's own background is `bg-muted` — a muted
        // chip on a muted row disappeared into it. White reads as a key cap
        // sitting on the highlight, which is what it's meant to be.
        'ml-auto inline-flex h-5 w-6 shrink-0 items-center justify-center self-center rounded border border-border bg-card text-muted-foreground',
        'invisible group-data-[selected=true]:visible',
        className,
      )}
    >
      <CornerDownLeft className="h-3 w-3" />
    </span>
  );
}

/**
 * Keyboard-hint row for the bottom of the palette / results popup — the
 * "↑↓ to navigate · ⏎ to select" strip.
 *
 * Only advertise a key the surface actually handles: the palette's arrow keys
 * and Enter come from cmdk, and the results popup's Esc comes from Radix. A
 * hint for a key nothing listens to is worse than no hint.
 */
export function KeyHint({ keys, label }: { keys: string[]; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="flex items-center gap-1">
        {keys.map((key) => (
          <kbd
            key={key}
            className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-muted px-1 font-sans text-[11px] font-medium text-muted-foreground"
          >
            {key}
          </kbd>
        ))}
      </span>
      <span>{label}</span>
    </span>
  );
}
